import { DELEGATE_V1_ABI } from '@revoke.cash/core/abis';
import type { Address } from 'viem';
import { AbstractDelegatePlatform } from './AbstractDelegatePlatform';
import type { Delegation, TransactionData } from './DelegatePlatform';

export class DelegateV1Platform extends AbstractDelegatePlatform {
  address = '0x00000000000076A84feF008CDAbe6409d2FE638B' as const;
  abi = DELEGATE_V1_ABI;
  name = 'Delegate.xyz V1';

  async getOutgoingDelegations(wallet: Address): Promise<Delegation[]> {
    const contractDelegationsPromise = this.publicClient.readContract({
      address: this.address,
      abi: this.abi,
      functionName: 'getContractLevelDelegations',
      args: [wallet],
    });

    const tokenDelegationsPromise = this.publicClient.readContract({
      address: this.address,
      abi: this.abi,
      functionName: 'getTokenLevelDelegations',
      args: [wallet],
    });

    const allDelegatesPromise = this.publicClient.readContract({
      address: this.address,
      abi: this.abi,
      functionName: 'getDelegatesForAll',
      args: [wallet],
    });

    const [contractDelegations, tokenDelegations, allDelegates] = await Promise.all([
      contractDelegationsPromise,
      tokenDelegationsPromise,
      allDelegatesPromise,
    ]);

    const delegations: Delegation[] = [];

    // Process all-level delegations
    allDelegates.forEach((delegate) => {
      delegations.push({
        type: 'WALLET',
        delegator: wallet,
        delegate,
        contract: null,
        tokenId: null,
        direction: 'OUTGOING',
        platform: this.name,
        chainId: this.chainId,
      });
    });

    // Process contract-level delegations
    contractDelegations.forEach(([contract, delegate]) => {
      delegations.push({
        type: 'CONTRACT',
        delegator: wallet,
        delegate,
        contract,
        tokenId: null,
        direction: 'OUTGOING',
        platform: this.name,
        chainId: this.chainId,
      });
    });

    // Process token-level delegations
    tokenDelegations.forEach(([contract, tokenId, delegate]) => {
      delegations.push({
        type: 'ERC721',
        delegator: wallet,
        delegate,
        contract,
        tokenId,
        direction: 'OUTGOING',
        platform: this.name,
        chainId: this.chainId,
      });
    });

    return delegations;
  }

  async getIncomingDelegations(wallet: Address): Promise<Delegation[]> {
    const delegationsRaw = await this.publicClient.readContract({
      address: this.address,
      abi: this.abi,
      functionName: 'getDelegationsByDelegate',
      args: [wallet],
    });

    const delegations: Delegation[] = [];

    delegationsRaw.forEach(([delegationType, delegator, delegate, contract, tokenId]) => {
      let type: Delegation['type'];
      switch (delegationType) {
        case 1:
          type = 'WALLET';
          break;
        case 2:
          type = 'CONTRACT';
          break;
        case 3:
          type = 'ERC721';
          break;
        default:
          type = 'NONE';
      }
      delegations.push({
        type,
        delegator,
        delegate,
        contract: type === 'WALLET' ? null : contract,
        tokenId: type === 'ERC721' ? tokenId : null,
        direction: 'INCOMING',
        platform: this.name,
        chainId: this.chainId,
      });
    });
    return delegations;
  }

  async prepareRevokeDelegationInternal(delegation: Delegation): Promise<TransactionData> {
    if (delegation.direction !== 'OUTGOING') {
      throw new Error('Cannot revoke incoming delegations');
    }

    if (delegation.type === 'WALLET') {
      return {
        address: this.address,
        abi: this.abi,
        functionName: 'revokeAllDelegates',
        args: [],
      };
    }

    return {
      address: this.address,
      abi: this.abi,
      functionName: 'revokeDelegate',
      args: [delegation.delegate],
    };
  }
}
